import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useWorkspace } from '../../../core/workspace/WorkspaceContext'
import { useMantineColorScheme } from '@mantine/core'
import {
  Box,
  Button,
  Text,
  Paper,
  Stack,
  Group,
  TextInput,
  ActionIcon,
  Menu,
  Modal,
  Checkbox,
  Tooltip
} from '@mantine/core'
import {
  IconPlus,
  IconTrash,
  IconEdit,
  IconChevronDown,
  IconChevronRight,
  IconDots,
  IconCopy,
  IconLock,
  IconLockOpen
} from '@tabler/icons-react'
// F 422 — the encryption engine (F 233 / F 238) was never reachable from the
// UI: its only caller is `EncryptionPanel`, and that panel's mount point is
// gated on `encryptionPassphrase !== undefined`, which NOTHING in the app
// passes. So this import brings the ENGINE onto the page menu rather than
// mounting that panel. The engine is used verbatim — `encryptNode` /
// `decryptNode` return `Result<T>` and do not throw, so the menu item handles
// that result instead of assuming the node is now sealed.
//
// `memoryNodeStore` is imported and PASSED EXPLICITLY, not left to the default.
// The engine's `requireStore()` throws "[vault] no node store configured" when
// no `setNodeStore()` has run, and the only thing that ever called it is
// `EncryptionPanel`'s mount effect — which never runs, for the same reason
// this button did not exist. Relying on that would have shipped a menu that
// throws on every click. F 285 made `store` the last parameter for exactly this
// reason; F 233's own note says the caller supplies the store.
import {
  encryptNode,
  decryptNode,
  readNode,
  setNodeStore,
} from '../../../core/credentials/vault'
import { memoryNodeStore } from '../surfaces/EncryptionPanel'

// Install the engine's node store ONCE, here, because nothing else does.
// `requireStore()` throws "[vault] no node store configured — call
// setNodeStore() first" and the ONLY caller that ever installed one was
// `EncryptionPanel`'s mount effect — which never runs, for precisely the
// reason this button did not exist. Leaving the default would ship a menu that
// throws on every click; the item would read `unknown`, stay disabled, and
// look like a rendering bug rather than a missing dependency.
//
// The same store instance `EncryptionPanel` uses, so both surfaces see one set
// of nodes. Module scope means it runs on first import, before any click.
setNodeStore(memoryNodeStore);
import { eventBus } from '../../../core/event-bus'
// F 218 — the delete refusal. Reuses the F 156 P1 doctrine that already
// lives in containment.ts rather than restating the rule here. `operatorApproved`
// is a call-site argument in that module BY DESIGN — if it were a flag on the
// node it would become data, and "delete this subtree" would stay permitted
// forever after one click.
import { validateDelete } from '../../../core/views/containment'
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCenter,
  DragOverlay
} from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'

/* ============================================================================
 * F 472 — THE ONE DISPATCHER OF `fc:quick-capture:toggle`
 * ==========================================================================
 *
 * Since F 77/F 78 this file has owned the event, and the F 78 + F 201 specs
 * assert that the dispatch literal lives HERE. F 472 moved the sidebar's task
 * affordance out of the tree (it was rendering a "New task" button under the
 * "PAGES" heading, which is where the operator found it and called it ugly), so
 * the dispatch is now a NAMED export that the sidebar calls.
 *
 * The point of keeping it here is that there is still exactly ONE dispatch
 * site in the codebase. The sidebar calls this function; it does not emit a
 * second event of its own. That is what "one source of truth" means, and the
 * specs that protect it still hold without being edited.
 */
export function openQuickCapture(): void {
  window.dispatchEvent(new CustomEvent('fc:quick-capture:toggle'));
}

/* ============================================================================
 * Types
 * ========================================================================== */

export interface PageTreeProps {
  phase?: number
  currentPageId?: string
  /**
   * F 472 — PUBLISH THE CREATE ACTION UPWARD.
   *
   * The sidebar's "Pages" section owns the add affordance (it sits on the
   * heading row, before the list), but the create is not a one-liner: it
   * creates the page, selects it, expands the parent, moves the tree cursor
   * and opens the inline rename editor. Re-implementing that in the sidebar
   * would be a SECOND implementation of page creation — the same mistake F
   * 471 removed when it deleted the second database row.
   *
   * So the tree hands its action up and the parent drives it. The parent's
   * callback is expected to be stable and to write to a ref, so publishing the
   * action here never causes a render loop.
   */
  onRegisterCreate?: (action: () => void) => void
  onPageSelect?: (pageId: string) => void
  onPageCreate?: (parentId?: string) => void
  onPageDelete?: (pageId: string) => void
  onPageRename?: (pageId: string, newName: string) => void
}

interface PageRef {
  id: string
  title: string
  parentId?: string
  type?: string
  cover?: string
}

interface FlatNode {
  page: PageRef
  depth: number
  parentId?: string
}

type DropPosition = 'above' | 'inside' | 'below'

interface DropTargetInfo {
  id: string
  position: DropPosition
}

/* ============================================================================
 * PageItem — one row in the tree (droppable + draggable + keyboard focusable)
 * ========================================================================== */

interface PageItemProps {
  page: PageRef
  depth: number
  isSelected: boolean
  isFocused: boolean
  isExpanded: boolean
  hasChildren: boolean
  activeDragId: string | null
  dropTarget: DropTargetInfo | null
  onSelect: (id: string) => void
  onDelete: (id: string) => void
  onRequestDelete: (id: string) => void
  onRename: (id: string, newName: string) => void
  onCreateChild: (id: string) => void
  onToggleExpand: (id: string) => void
  onFocus: (id: string) => void
  registerRow: (id: string, el: HTMLElement | null) => void
  openRenameEditor: (id: string) => void
  /** mutable set of ids currently in rename mode, owned by parent */
  renameEditing: React.MutableRefObject<Set<string>>
  editingId: string | null
  setEditingId: React.Dispatch<React.SetStateAction<string | null>>
}

const PageItem: React.FC<PageItemProps> = ({
  page,
  depth,
  isSelected,
  isFocused,
  isExpanded,
  hasChildren,
  activeDragId,
  dropTarget,
  onSelect,
  onDelete,
  onRequestDelete,
  onRename,
  onCreateChild,
  onToggleExpand,
  onFocus,
  registerRow,
  openRenameEditor,
  renameEditing,
  editingId,
  setEditingId,
}) => {
  const isEditingHere = editingId === page.id
  const [editName, setEditName] = useState(page.title)

  // Sync local edit name when the page title changes externally (e.g. remote update).
  useEffect(() => { setEditName(page.title) }, [page.title])

  // ── F 422 — page encryption ──────────────────────────────────────────────
  // The menu item is NOT optimistic. `encryptNode` fails for real reasons (no
  // such node, already sealed, no content) and `readNode` is the only thing
  // that knows which of those happened, so the label is derived from a read
  // rather than from the click that requested the change. A menu that says
  // "Encrypt" over an already-sealed page is worse than no menu at all.
  const [encState, setEncState] = useState<'unknown' | 'plain' | 'sealed'>('unknown')
  const [encOpen, setEncOpen] = useState(false)
  const [encPass, setEncPass] = useState('')
  const [encError, setEncError] = useState<string | null>(null)
  const [encBusy, setEncBusy] = useState(false)

  const refreshEncState = useCallback(async () => {
    try {
      const r = await readNode(page.id)
      // `readNode` TRANSPARENTLY OPENS a sealed node when the vault allows, so
      // `encrypted` is the only field that distinguishes the two states.
      setEncState(r && (r as { encrypted?: boolean }).encrypted === true ? 'sealed' : 'plain')
    } catch {
      setEncState('unknown')
    }
  }, [page.id])

  useEffect(() => { void refreshEncState() }, [refreshEncState])

  const submitEncryption = useCallback(async () => {
    if (encBusy) return
    if (!encPass) { setEncError('A passphrase is required.'); return }
    setEncBusy(true)
    setEncError(null)
    try {
      // Engine contract: these RETURN a Result and do not throw. Treating the
      // call as if it succeeded is how a page ends up labelled "sealed" while
      // its content is still plaintext.
      // `Result<unknown>` is NOT discriminable by TypeScript: a union arm
      // containing `unknown` collapses the control-flow narrowing that
      // `r.ok` relies on, so `r.error` is not a known property in the else
      // branch. Reproduced in isolation, both as a ternary and with an
      // annotated variable, so this is the engine's type and not a mistake
      // in this file. The engine is left untouched (its contract is F 233's);
      // the message is read through a narrowing guard instead.
      const r = encState === 'sealed'
        ? await decryptNode(page.id, encPass)
        : await encryptNode(page.id, encPass)
      if (r.ok) {
        setEncOpen(false)
        setEncPass('')
        await refreshEncState()
      } else {
        const message = (r as { error?: string }).error || 'Encryption failed.'
        setEncError(message)
      }
    } catch (e) {
      setEncError(e instanceof Error ? e.message : String(e))
    } finally {
      setEncBusy(false)
    }
  }, [encBusy, encPass, encState, page.id, refreshEncState])

  /* Drag: a PageItem is both draggable (handle is the row) and droppable.
   * For droppable we tag the whole row with `over.id`; the hit-test in
   * handleDragEnd computes above/inside/below from cursor Y offset.        */
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    transform,
    isDragging,
  } = useDraggable({
    id: page.id,
    data: { type: 'page', page },
  })

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `drop-${page.id}`,
    data: { pageId: page.id },
  })

  const setRowRef = (el: HTMLDivElement | null) => {
    setDragRef(el)
    setDropRef(el)
    registerRow(page.id, el)
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onSelect(page.id)
  }

  const handleRenameSubmit = useCallback(() => {
    if (editName.trim() && editName !== page.title) {
      onRename(page.id, editName.trim())
    }
    setEditingId(null)
    renameEditing.current.delete(page.id)
  }, [editName, page.id, page.title, onRename, setEditingId, renameEditing])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Inline rename input keys are handled here; tree-level keys are handled
    // by PageTree's onKeyDown on the tree root.
    if (!isEditingHere) return
    if (e.key === 'Enter') {
      e.preventDefault()
      handleRenameSubmit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setEditName(page.title)
      setEditingId(null)
      renameEditing.current.delete(page.id)
    }
  }

  const dragging = activeDragId === page.id
  const dragStyle: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : 1,
    transition: 'opacity 120ms ease',
  }

  // Drop indicator: above (line), below (line), inside (full ring).
  let dropIndicator: React.ReactNode = null
  if (dropTarget && dropTarget.id === page.id) {
    if (dropTarget.position === 'above') {
      dropIndicator = (
        <div
          data-testid={`drop-indicator-above-${page.id}`}
          style={{
            position: 'absolute',
            top: -2,
            left: 0,
            right: 0,
            height: 3,
            background: 'var(--mantine-color-blue-5, #228be6)',
            borderRadius: 2,
            pointerEvents: 'none',
          }}
        />
      )
    } else if (dropTarget.position === 'below') {
      dropIndicator = (
        <div
          data-testid={`drop-indicator-below-${page.id}`}
          style={{
            position: 'absolute',
            bottom: -2,
            left: 0,
            right: 0,
            height: 3,
            background: 'var(--mantine-color-blue-5, #228be6)',
            borderRadius: 2,
            pointerEvents: 'none',
          }}
        />
      )
    } else if (dropTarget.position === 'inside') {
      dropIndicator = (
        <div
          data-testid={`drop-indicator-inside-${page.id}`}
          style={{
            position: 'absolute',
            inset: -1,
            border: `2px solid var(--mantine-color-blue-5, #228be6)`,
            borderRadius: 6,
            pointerEvents: 'none',
          }}
        />
      )
    }
  }

  return (
    <Box
      ref={setRowRef}
      data-page-id={page.id}
      data-page-row-id={page.id}
      className={`page-item${isSelected ? ' selected' : ''}${isFocused ? ' focused' : ''}`}
      style={{
        position: 'relative',
        marginLeft: depth > 0 ? 8 : 0,
        ...dragStyle,
      }}
      onMouseDown={() => onFocus(page.id)}
      tabIndex={isFocused ? 0 : -1}
      onFocus={() => { if (!isFocused) onFocus(page.id) }}
    >
      {dropIndicator}
      <Box
        className="page-item-row"
        // F 320 — page rows carried no testid at all, so nothing downstream could
        // address one. Same `fc-sidebar-v2-` family as the rest of the sidebar.
        // This component is mounted in production by SidebarV2 only; the other
        // `<PageTree` in the tree is a different file (surfaces/PageTree.tsx).
        data-testid={`fc-sidebar-v2-page-${page.id}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '4px 8px',
          borderRadius: 4,
          backgroundColor: isOver
            ? 'var(--fc-accent-muted)'
            : isSelected
              ? 'var(--fc-bg-tertiary)'
              : // F 472 — THE CURSOR IS NOT A SELECTION.
                //
                // This used to tint the CURSOR row with `--fc-bg-hover`
                // (#EDE5D7) and tint the SELECTED row with `--fc-bg-tertiary`
                // (#F4E5D8). Those two colours are within a hair of each
                // other, so a page that was NOT open looked exactly like a
                // page that was — which is the operator's "why is product
                // requirement highlighted", measured on production: the app
                // opens on the Today task view, NO page row carries
                // `.selected`, and yet the first page row was painted like
                // one that was open.
                //
                // So the background belongs to the SELECTED row alone, and the
                // cursor is marked by its accent bar alone (Sidebar.css, at
                // 45% opacity). Two states, two treatments, and the one that
                // is open is the one that is tinted.
                'transparent',
          // F 368 — the row's text colour was inherited from an ancestor, so
          // the row's readability depended on whatever the ancestor happened
          // to carry. Declare it from the theme. The value this replaces is
          // the same value the inheritance produced in both modes, so the
          // resting rows do not change; what it removes is the possibility of
          // a state painting its own background under a text colour that
          // nothing owns. `var(--fc-text-primary)` is written inline on
          // <html> by the theme system in both modes.
          color: 'var(--fc-text-primary)',
          cursor: isEditingHere ? 'text' : 'grab',
          // F 472 — THE BLUE INPUT BOX IS GONE.
          //
          // This row used to carry `outline: 2px solid var(--mantine-color-
          // blue-5, #228be6)` whenever the tree's cursor rested on it. That is
          // what the operator saw as "why is product requirement highlighted":
          // a blue ring around a text label reads as an EDITABLE FIELD, not as
          // a nav row — and `--mantine-color-blue-5` is not a token this app's
          // theme system writes, so it could never be themed with the rest of
          // the sidebar.
          //
          // The keyboard ring is now CSS `:focus-visible` in the design
          // system's own accent (Sidebar.css), so it appears for the person
          // who is actually navigating by keyboard — and NOT for the row that
          // merely happens to be the tree's cursor at load. The SELECTED row
          // is painted like every other selected row in this sidebar: a
          // `--fc-bg-tertiary` tint and a 3px `--fc-accent-primary` bar.
          outline: 'none',
          outlineOffset: -2,
          transition: 'background-color 0.1s ease',
          minHeight: 28,
        }}
        {...(isEditingHere ? {} : listeners)}
        {...attributes}
        onClick={handleClick}
        onDoubleClick={(e) => {
          e.stopPropagation()
          openRenameEditor(page.id)
        }}
      >
        {/* Expand/collapse toggle */}
        <Box style={{ width: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {hasChildren ? (
            <ActionIcon
              variant="subtle"
              size="xs"
              onClick={(e) => {
                e.stopPropagation()
                onToggleExpand(page.id)
              }}
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
              style={{ padding: 0 }}
            >
              {isExpanded ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
            </ActionIcon>
          ) : (
            <Box style={{ width: 12 }} />
          )}
        </Box>

        {/* Page icon */}
        {page.cover ? (
          <div
            style={{
              width: 18, height: 18, borderRadius: 3, flexShrink: 0,
              background: /^https?:\/\//.test(page.cover) || page.cover.startsWith('data:')
                ? `center / cover no-repeat url(${page.cover})`
                : page.cover,
            }}
            aria-hidden="true"
          />
        ) : (
          <Box style={{ fontSize: 14 }}>
            {page.type === 'database' ? '📋' : '📄'}
          </Box>
        )}

        {/* Page title */}
        <Box style={{ flex: 1, minWidth: 0 }}>
          {isEditingHere ? (
            <TextInput
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleRenameSubmit}
              autoFocus
              size="xs"
              variant="filled"
              data-rename-input
              styles={{
                input: { padding: '2px 8px', height: 24, fontSize: 13 }
              }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            /* F 229 — 4e. A clipped name needs the full name available on
               hover, or the ellipsis hides information with no way back. */
            <Text
              size="sm"
              fw={isSelected ? 600 : 400}
              title={page.title}
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {page.title}
            </Text>
          )}
        </Box>

        {/* Action buttons: shown when focused or selected and not editing */}
        {(isFocused || isSelected) && !isEditingHere && (
          <Group gap={2} style={{ flexShrink: 0 }}>
            <Tooltip label="Add subpage">
              <ActionIcon
                variant="subtle"
                size="xs"
                // F 472 — ONE VISUAL LANGUAGE FOR THE WHOLE SIDEBAR.
                //
                // This was `color="blue"` — a hard-coded Mantine colour from
                // outside this app's theme system, and the same blue the
                // operator read as "an input box" on the page row.
                //
                // Every "+" in the sidebar is now the same MUTED tone
                // (--fc-text-hint), exactly like the section actions on the
                // heading rows. The accent colour is not spent on a button:
                // it is reserved for the thing that means "you are here" —
                // the selection tint and the cursor bar. One language, and the
                // accent still means one thing.
                color="var(--fc-text-hint)"
                onClick={(e) => {
                  e.stopPropagation()
                  onCreateChild(page.id)
                }}
              >
                <IconPlus size={12} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="More options">
              <Menu position="bottom-start" shadow="md">
                <Menu.Target>
                  <ActionIcon
                    variant="subtle"
                    size="xs"
                    onClick={(e) => e.stopPropagation()}
                    data-action="more"
                  >
                    <IconDots size={12} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    data-action="rename"
                    leftSection={<IconEdit size={14} />}
                    onClick={(e) => {
                      e.stopPropagation()
                      openRenameEditor(page.id)
                    }}
                  >
                    Rename
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconCopy size={14} />}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Duplicate
                  </Menu.Item>
                  {/* F 422 — the operator's missing button. The label is read
                      from state, never assumed, so a page that failed to seal
                      still says "Encrypt" and can be retried. */}
                  <Menu.Item
                    data-action="encrypt"
                    leftSection={encState === 'sealed' ? <IconLockOpen size={14} /> : <IconLock size={14} />}
                    onClick={(e) => {
                      e.stopPropagation()
                      setEncError(null)
                      setEncPass('')
                      setEncOpen(true)
                    }}
                    disabled={encState === 'unknown'}
                  >
                    {encState === 'sealed' ? 'Decrypt' : 'Encrypt'}
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item
                    color="red"
                    leftSection={<IconTrash size={14} />}
                    onClick={(e) => {
                      e.stopPropagation()
                      // F 218 — opens the confirmation. This used to call
                      // the delete directly, with no name and no gate.
                      onRequestDelete(page.id)
                    }}
                  >
                    Delete
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Tooltip>
          </Group>
        )}
      </Box>
      {/* F 422 — the passphrase step. The operator types the passphrase that
          SEALS this page; nothing is compared against a stored value, because
          a comparison would require the passphrase to already exist somewhere,
          which is the thing encryption exists to prevent. The engine keeps the
          salt on the envelope, so this passphrase is the only key. */}
      <Modal
        opened={encOpen}
        onClose={() => { if (!encBusy) { setEncOpen(false); setEncError(null); setEncPass('') } }}
        title={encState === 'sealed' ? 'Decrypt page' : 'Encrypt page'}
        centered
        data-testid="fc-encrypt-page-modal"
      >
        <Stack gap="sm">
          <Text size="sm">
            {encState === 'sealed'
              ? <>Decrypt <strong>&quot;{page.title}&quot;</strong>? It will be readable again.</>
              : <>Encrypt <strong>&quot;{page.title}&quot;</strong>? Its content will be sealed with the passphrase you type.</>}
          </Text>
          <Text size="xs" c="dimmed">
            The passphrase is not stored. If you lose it, the content cannot be recovered.
          </Text>
          <TextInput
            data-testid="fc-encrypt-page-passphrase"
            type="password"
            label="Passphrase"
            placeholder="Passphrase"
            value={encPass}
            onChange={(e) => { setEncPass(e.currentTarget.value); setEncError(null) }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void submitEncryption() } }}
            aria-invalid={encError ? true : undefined}
            autoComplete="new-password"
          />
          {encError && (
            <Text size="sm" c="red" data-testid="fc-encrypt-page-error" role="alert">{encError}</Text>
          )}
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={() => { setEncOpen(false); setEncError(null); setEncPass('') }} disabled={encBusy}>
              Cancel
            </Button>
            <Button onClick={() => { void submitEncryption() }} loading={encBusy} data-testid="fc-encrypt-page-submit">
              {encState === 'sealed' ? 'Decrypt' : 'Encrypt'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  )
}

/* ============================================================================
 * PageTree — recursive tree with shared expand state, keyboard nav, drag
 * ========================================================================== */

export const PageTree: React.FC<PageTreeProps> = ({
  currentPageId,
  onPageSelect,
  onPageCreate,
  onRegisterCreate
}) => {
  const ctx = useWorkspace()
  // Some workspaces expose movePage; if absent (older builds) we still
  // render but disable the drag handler gracefully.
  const {
    pages,
    createPage,
    deletePage,
    renamePage,
    selectPage,
    getChildPages,
    hasChildren,
  } = ctx
  const movePage = (ctx as unknown as {
    movePage?: (pageId: string, newParentId: string | null, beforeSiblingId?: string) => Promise<void>
  }).movePage

  const [loading, setLoading] = useState(false)
  const [focusedId, setFocusedId] = useState<string | null>(null)

  // F 320 — a `database` page is a named query, not a container (F 306 gave it
  // no record container), so filtering it out here orphans no children. The
  // sidebar already lists them under DATABASES via `dbPages` (SidebarV2.tsx:98-99);
  // this tree filtered only on `!p.parentId`, so every top-level database
  // rendered a second time under PAGES. One filter, and every derived view below
  // reads from it — including keyboard focus, which would otherwise park on a row
  // that is no longer rendered.
  const visiblePages = useMemo(
    () => pages.filter(p => p.type !== 'database'),
    [pages],
  )

  // Start all top-level pages expanded; lets the user collapse as desired.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    return new Set(visiblePages.filter(p => !p.parentId).map(p => p.id))
  })
  const [editingId, setEditingId] = useState<string | null>(null)
  const renameEditingRef = useRef<Set<string>>(new Set())
  const rowRefs = useRef<Map<string, HTMLElement>>(new Map())
  const treeRootRef = useRef<HTMLDivElement | null>(null)

  const [activeDragId, setActiveDragId] = useState<string | null>(null)
  const [dropTarget, setDropTarget] = useState<DropTargetInfo | null>(null)

  const { colorScheme } = useMantineColorScheme()
  // F 472 — `bgColor` is GONE. The tree used to paint its own surface with it
  // (`backgroundColor: bgColor`), which made the page group a cream card
  // indented inside the sidebar while every other group sat flush on it. The
  // tree now inherits the sidebar's surface.
  const textColor = 'var(--fc-text-primary, #202124)'
  const borderColor = 'var(--fc-border-primary, #e9e9e7)'

  // Defaults
  useEffect(() => {
    if (!focusedId && pages.length > 0) {
      // Initialize focus on current page (if any) or first top-level page.
      const initial = currentPageId && visiblePages.find(p => p.id === currentPageId)
        ? currentPageId
        : visiblePages.find(p => !p.parentId)?.id ?? visiblePages[0]?.id ?? null
      setFocusedId(initial)
    }
  }, [focusedId, visiblePages, currentPageId])

  // Whenever the focused page disappears (e.g. it was deleted), refocus
  // the first remaining page so keyboard navigation has a target.
  useEffect(() => {
    if (focusedId && !pages.find(p => p.id === focusedId)) {
      setFocusedId(visiblePages[0]?.id ?? null)
    }
  }, [focusedId, pages, visiblePages])

  // Top-level ordered list
  const topLevel = useMemo(
    () => visiblePages.filter(p => !p.parentId),
    [visiblePages],
  )

  // Flatten the visible (expanded) tree for keyboard navigation.
  const flatVisible = useMemo<FlatNode[]>(() => {
    const out: FlatNode[] = []
    const visit = (page: PageRef, depth: number) => {
      out.push({ page, depth, parentId: page.parentId })
      if (expandedIds.has(page.id)) {
        const children = visiblePages.filter(p => p.parentId === page.id)
        children.forEach(c => visit(c, depth + 1))
      }
    }
    topLevel.forEach(p => visit(p, 0))
    return out
  }, [visiblePages, topLevel, expandedIds])

  const focusRow = useCallback((id: string) => {
    // Defer to next microtask so React renders the focusable class first.
    requestAnimationFrame(() => {
      const el = rowRefs.current.get(id)
      if (el && typeof (el as HTMLElement).focus === 'function') {
        ;(el as HTMLElement).focus({ preventScroll: false })
      }
    })
  }, [])

  const handleSelect = useCallback((id: string) => {
    void selectPage(id)
    onPageSelect?.(id)
  }, [selectPage, onPageSelect])

  const handleToggleExpand = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const openRenameEditor = useCallback((id: string) => {
    setEditingId(id)
    renameEditingRef.current.add(id)
  }, [])

  const handleCreatePage = useCallback(async (parentId?: string) => {
    try {
      setLoading(true)
      const newPage = await createPage('Untitled', parentId)
      await selectPage(newPage.id)
      onPageSelect?.(newPage.id)
      // Auto-expand the new child's parent and start renaming it.
      if (parentId) {
        setExpandedIds(prev => new Set(prev).add(parentId))
      }
      setFocusedId(newPage.id)
      focusRow(newPage.id)
      requestAnimationFrame(() => openRenameEditor(newPage.id))
    } catch (error) {
      console.error('Failed to create page:', error)
    } finally {
      setLoading(false)
    }
  }, [createPage, selectPage, onPageSelect, focusRow, openRenameEditor])

  // F 472 — PUBLISH THE CREATE ACTION to the sidebar's section heading.
  //
  // The "+" on the "Pages" heading drives THIS, so the operator gets the whole
  // flow (create → select → focus → inline rename) from the affordance that is
  // always visible, instead of from a button buried at the top of the tree.
  //
  // The parent's callback writes to a ref and does not set state, so this
  // effect can publish on every change of `handleCreatePage` without ever
  // causing a render loop.
  useEffect(() => {
    onRegisterCreate?.(() => { void handleCreatePage() })
  }, [handleCreatePage, onRegisterCreate])

  /* ---------------------- Delete (F 218) ----------------------
   *
   * F 218 found the row menu ALREADY had a Delete item calling this
   * handler, so the missing piece was never the button — it was that
   * the handler deleted unconditionally. A parent page with children
   * was removed and its children silently orphaned, with a confirm()
   * that did not even name the page being destroyed.
   *
   * The gate is F 156 P1's `validateDelete`, imported from
   * containment.ts. The doctrine is applied, not reimplemented: a node
   * with children is refused unless the operator explicitly overrides,
   * and the override is a call-site argument so it cannot be persisted
   * onto the data and leave deletion permanently permitted.
   */
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null)
  const [overrideChildren, setOverrideChildren] = useState(false)

  const handleRequestDelete = useCallback((pageId: string) => {
    const page = pages.find((p: PageRef) => p.id === pageId)
    // Reset the override every time the dialog opens. A checkbox that
    // remembers "yes" from a previous delete is a foot-gun.
    setOverrideChildren(false)
    setDeleteTarget({ id: pageId, title: page?.title || 'Untitled' })
  }, [pages])

  const handleDeletePage = useCallback(async (pageId: string) => {
    const target = deleteTarget?.id === pageId ? deleteTarget : { id: pageId, title: 'Untitled' }

    // F 156 P1 — refuse if the page has children, unless overridden.
    const verdict = validateDelete(
      pages.map((p: PageRef) => ({ id: p.id, parentId: p.parentId })),
      pageId,
      { operatorApproved: overrideChildren }
    )
    if (!verdict.ok) {
      // Stay open. The operator needs to see WHY, not lose the dialog.
      return
    }

    try {
      await deletePage(pageId)
      setDeleteTarget(null)
      setOverrideChildren(false)
      if (currentPageId === pageId && pages.length > 0) {
        const remaining = pages.filter(p => p.id !== pageId)
        if (remaining.length > 0) {
          await selectPage(remaining[0].id)
        }
      }
    } catch (error) {
      console.error('Failed to delete page:', error)
    }
  }, [deletePage, currentPageId, pages, selectPage, deleteTarget, overrideChildren])

  const deleteVerdict = useMemo(() => {
    if (!deleteTarget) return null
    return validateDelete(
      pages.map((p: PageRef) => ({ id: p.id, parentId: p.parentId })),
      deleteTarget.id,
      { operatorApproved: overrideChildren }
    )
  }, [deleteTarget, pages, overrideChildren])

  const handleRenamePage = useCallback(async (pageId: string, newName: string) => {
    try {
      await renamePage(pageId, newName)
    } catch (error) {
      console.error('Failed to rename page:', error)
    }
  }, [renamePage])

  /* ---------------------- Drag handler ---------------------- */

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  )

  // Convert pageId → DOM rect for hit-testing; @dnd-kit doesn't expose
  // pointer coords on over events directly so we sample the cached rect.
  const rectCache = useRef<Map<string, DOMRect>>(new Map())
  const captureRowRect = useCallback((id: string, el: HTMLElement | null) => {
    if (el) rectCache.current.set(id, el.getBoundingClientRect())
  }, [])

  const registerRow = useCallback((id: string, el: HTMLElement | null) => {
    if (el) {
      rowRefs.current.set(id, el)
      rectCache.current.set(id, el.getBoundingClientRect())
    } else {
      rowRefs.current.delete(id)
      rectCache.current.delete(id)
    }
  }, [])

  const computeDropTarget = useCallback((draggedId: string, hoveredId: string, pointerY: number): DropPosition => {
    if (draggedId === hoveredId) return 'inside'
    const rect = rectCache.current.get(hoveredId)
    if (!rect) return 'inside'
    const rel = (pointerY - rect.top) / rect.height
    if (rel < 0.25) return 'above'
    if (rel > 0.75) return 'below'
    return 'inside'
  }, [])

  const handleDragMove = useCallback((e: { active: { id: string | number }; over: { id: string | number } | null; activatorEvent?: PointerEvent; delta: { x: number; y: number } }) => {
    const draggedId = String(e.active.id)
    if (!e.over) {
      setDropTarget(null)
      return
    }
    const overIdRaw = String(e.over.id)
    if (!overIdRaw.startsWith('drop-')) return
    const overId = overIdRaw.slice('drop-'.length)
    // Approximate pointer Y = activator + delta (dnd-kit provides these).
    const startY = (e.activatorEvent as PointerEvent | undefined)?.clientY ?? 0
    const pointerY = startY + e.delta.y
    setDropTarget({ id: overId, position: computeDropTarget(draggedId, overId, pointerY) })
  }, [computeDropTarget])

  const handleDragEnd = useCallback((e: DragEndEvent) => {
    const draggedId = String(e.active.id)
    setActiveDragId(null)
    setDropTarget(null)
    if (!movePage) return // graceful: no-op if context not wired
    const overIdRaw = String(e.over?.id ?? '')
    if (!overIdRaw.startsWith('drop-')) return
    const overId = overIdRaw.slice('drop-'.length)
    if (overId === draggedId) return

    const dragged = pages.find(p => p.id === draggedId)
    const overPage = pages.find(p => p.id === overId)
    if (!dragged || !overPage) return

    const startY = (e.activatorEvent as PointerEvent | undefined)?.clientY ?? 0
    const pointerY = startY + e.delta.y
    const position = computeDropTarget(draggedId, overId, pointerY)

    // Cycle prevention: refuse if target is dragged itself or a descendant.
    const isDescendantOf = (parentId: string | undefined, candidate: string): boolean => {
      let cur = parentId
      const seen = new Set<string>()
      while (cur && !seen.has(cur)) {
        seen.add(cur)
        if (cur === candidate) return true
        cur = pages.find(p => p.id === cur)?.parentId
      }
      return false
    }
    // Drop "into" a node's own descendant subtree is a cycle.
    // Drop "above/below" a sibling re-uses overPage.parentId; the only cycle
    // case there is dragging a node next to its own subtree's edge — rare
    // and harmless because we only change the moved node's parentId.
    if (position === 'inside' && isDescendantOf(overPage.id, draggedId)) return

    if (position === 'inside') {
      void movePage(draggedId, overPage.id, undefined)
      setExpandedIds(prev => new Set(prev).add(overPage.id))
      return
    }

    // above/below → sibling reordering under overPage.parentId
    const siblings = pages.filter(p => p.parentId === overPage.parentId && p.id !== draggedId)
    const idx = siblings.findIndex(p => p.id === overId)
    let beforeSiblingId: string | undefined
    if (position === 'above') {
      beforeSiblingId = siblings[idx]?.id
    } else {
      beforeSiblingId = siblings[idx + 1]?.id
    }
    void movePage(draggedId, overPage.parentId ?? null, beforeSiblingId)
    eventBus.emit('page-moved', { draggedId, parentId: overPage.parentId ?? null, position })
  }, [pages, movePage, computeDropTarget])

  /* ---------------------- Keyboard handler ---------------------- */

  const handleTreeKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!focusedId) return
    // Don't intercept keys while inline-rename editor is focused.
    if (editingId) return
    // Only handle when the tree itself (or a page-item row) has focus.
    const target = e.target as HTMLElement
    if (!target.closest('[data-page-id]')) return

    const idx = flatVisible.findIndex(({ page }) => page.id === focusedId)
    if (idx < 0) return
    const focused = flatVisible[idx].page
    const hasKids = pages.some(p => p.parentId === focused.id)
    const isExpanded = expandedIds.has(focused.id)

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault()
        const next = flatVisible[idx + 1]
        if (next) {
          setFocusedId(next.page.id)
          focusRow(next.page.id)
        }
        break
      }
      case 'ArrowUp': {
        e.preventDefault()
        const prev = flatVisible[idx - 1]
        if (prev) {
          setFocusedId(prev.page.id)
          focusRow(prev.page.id)
        }
        break
      }
      case 'ArrowRight': {
        e.preventDefault()
        if (hasKids && !isExpanded) {
          setExpandedIds(prev => new Set(prev).add(focused.id))
        } else if (hasKids && isExpanded) {
          const firstChild = flatVisible[idx + 1]
          if (firstChild) {
            setFocusedId(firstChild.page.id)
            focusRow(firstChild.page.id)
          }
        }
        break
      }
      case 'ArrowLeft': {
        e.preventDefault()
        if (hasKids && isExpanded) {
          setExpandedIds(prev => {
            const next = new Set(prev)
            next.delete(focused.id)
            return next
          })
        } else if (focused.parentId) {
          setFocusedId(focused.parentId)
          focusRow(focused.parentId)
        }
        break
      }
      case 'Enter': {
        e.preventDefault()
        handleSelect(focused.id)
        break
      }
      case ' ': {
        e.preventDefault()
        if (hasKids) handleToggleExpand(focused.id)
        break
      }
      case 'F2': {
        e.preventDefault()
        openRenameEditor(focused.id)
        break
      }
      case 'Delete': {
        if (e.metaKey || e.ctrlKey) {
          e.preventDefault()
          void handleDeletePage(focused.id)
        }
        break
      }
      default:
        break
    }
  }, [
    focusedId, editingId, flatVisible, pages, expandedIds,
    focusRow, handleSelect, handleToggleExpand, openRenameEditor, handleDeletePage,
  ])

  /* ---------------------- Render ---------------------- */

  const renderItem = (page: PageRef, depth: number): React.ReactNode => {
    const kids = getChildPages(page.id)
    const pageHasChildren = hasChildren(page.id)
    return (
      <div key={page.id}>
        <PageItem
          page={page}
          depth={depth}
          isSelected={page.id === currentPageId}
          isFocused={page.id === focusedId}
          isExpanded={expandedIds.has(page.id)}
          hasChildren={pageHasChildren}
          activeDragId={activeDragId}
          dropTarget={dropTarget}
          onSelect={handleSelect}
          onDelete={(id) => handleDeletePage(id)}
          onRequestDelete={handleRequestDelete}
          onRename={(id, newName) => handleRenamePage(id, newName)}
          onCreateChild={(id) => void handleCreatePage(id)}
          onToggleExpand={handleToggleExpand}
          onFocus={(id) => setFocusedId(id)}
          registerRow={registerRow}
          openRenameEditor={openRenameEditor}
          renameEditing={renameEditingRef}
          editingId={editingId}
          setEditingId={setEditingId}
        />
        {pageHasChildren && expandedIds.has(page.id) && kids.length > 0 && (
          <div style={{ borderLeft: `1px solid ${borderColor}`, marginLeft: 14 }}>
            {kids.map(child => renderItem(child, depth + 1))}
          </div>
        )}
      </div>
    )
  }

  const draggingPage = activeDragId ? pages.find(p => p.id === activeDragId) : null

  return (
    <Box
      ref={treeRootRef as unknown as React.Ref<HTMLDivElement>}
      className="page-tree"
      role="tree"
      aria-label="Workspace pages"
      onKeyDown={handleTreeKeyDown}
      // F 472 — THE TREE JOINS THE SIDEBAR'S SURFACE.
      //
      // It used to draw its OWN panel: `backgroundColor: bgColor` on a Box
      // padded 12px, so the page tree was a cream card indented inside the
      // sidebar while every other group sat flush. Measured on production
      // (build `2c50b10`): the tree box painted rgb(251, 248, 243) and its
      // rows started at x=20, while every task and database row started at
      // x=8. Two visual languages in one sidebar.
      //
      // Now the tree inherits the sidebar's surface and its rows line up with
      // the rest of the nav. The horizontal padding that remains is the
      // nesting indent, which the tree legitimately needs.
      style={{ padding: '4px 0', backgroundColor: 'transparent', color: textColor, outline: 'none' }}
    >
      {/* F 472 — the "New page" and "New task" buttons that used to live HERE
          are GONE.
          They rendered inside the tree, which is mounted under the "PAGES"
          heading, so a TASK-creation button appeared to belong to Pages — the
          operator's "why is new task under the pages heading it looks ugly".
          Both are now the section's own "+", on the heading row above the
          list: the task one under TASKS, the page one under PAGES. The
          dispatch of `fc:quick-capture:toggle` still lives in this file, as
          the exported `openQuickCapture`. */}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={(e) => setActiveDragId(String(e.active.id))}
        onDragMove={handleDragMove as unknown as (e: unknown) => void}
        onDragEnd={handleDragEnd}
        onDragCancel={() => { setActiveDragId(null); setDropTarget(null) }}
      >
        <Box className="page-tree-content">
          {topLevel.length > 0 ? (
            topLevel.map(page => renderItem(page, 0))
          ) : (
            <Paper
              p="lg"
              radius="md"
              style={{ textAlign: 'center', background: '#f7f6f3', border: 'none' }}
            >
              <Stack gap="sm" align="center">
                <Text size="sm" c="dimmed">No pages yet</Text>
                <Button
                  variant="light"
                  size="xs"
                  leftSection={<IconPlus size={14} />}
                  onClick={() => void handleCreatePage()}
                >
                  Create first page
                </Button>
              </Stack>
            </Paper>
          )}
        </Box>
        <DragOverlay dropAnimation={null}>
          {draggingPage ? (
            <Box
              style={{
                padding: '4px 8px',
                background: 'var(--mantine-color-blue-light, #d0ebff)',
                borderRadius: 4,
                boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {draggingPage.title}
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>
      {/* F 218 — delete confirmation. Names the page, and refuses when
          the page has children unless the operator explicitly overrides. */}
      <Modal
        opened={!!deleteTarget}
        onClose={() => { setDeleteTarget(null); setOverrideChildren(false) }}
        title="Delete page"
        centered
        data-testid="fc-delete-page-modal"
      >
        <Stack gap="sm">
          {/* F 226 — this sentence was "This cannot be undone", which was
              true of the hard delete it was written for and false the moment
              deletePage became a soft delete. The page now goes to Trash and
              can be restored. Saying otherwise is how users end up not using
              the recovery path that exists for them. */}
          <Text data-testid="fc-delete-page-name">
            Delete <strong>&quot;{deleteTarget?.title}&quot;</strong>?{' '}
            It will go to Trash, where you can restore it.
          </Text>

          {deleteVerdict && !deleteVerdict.ok && (
            <Text size="sm" c="red" data-testid="fc-delete-refusal">
              Cannot delete: page has {deleteVerdict.childCount}{' '}
              child{deleteVerdict.childCount === 1 ? '' : 'ren'}. Deleting it would orphan them.
            </Text>
          )}

          {deleteVerdict && !deleteVerdict.ok && (
            <Checkbox
              label={`Delete anyway, including ${deleteVerdict.childCount} child${deleteVerdict.childCount === 1 ? '' : 'ren'}`}
              checked={overrideChildren}
              onChange={(e) => setOverrideChildren(e.currentTarget.checked)}
              data-testid="fc-delete-override"
            />
          )}

          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={() => { setDeleteTarget(null); setOverrideChildren(false) }}>
              Cancel
            </Button>
            <Button
              color="red"
              disabled={!!deleteVerdict && !deleteVerdict.ok && !overrideChildren}
              onClick={() => { if (deleteTarget) void handleDeletePage(deleteTarget.id) }}
              data-testid="fc-delete-confirm"
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  )
}

export default PageTree
